import { useEffect, useState } from 'react'
import { applyConsent } from '../lib/analytics'

/** localStorage key shared with the consent bootstrap in index.html and src/lib/analytics.js. */
const CONSENT_STORAGE_KEY = 'cookieConsent'
const CONSENT_ACCEPTED = 'accepted'
const CONSENT_REJECTED = 'rejected'

/** Dispatched by the footer's "cookie preferences" key: the banner comes back. */
export const CONSENT_REOPEN_EVENT = 'anka:consent-open'

const translations = {
    tr: {
        message: 'Ziyaretleri ölçmek için Google Analytics çerezlerini yalnızca onayınızla kullanıyoruz. Tercihinizi sayfanın altındaki "Çerez tercihleri" ile istediğiniz zaman değiştirebilirsiniz.',
        privacy: 'Gizlilik Politikası',
        reject: 'Reddet',
        accept: 'Kabul Et',
    },
    en: {
        message: 'With your consent, we use Google Analytics cookies to measure visits. You can change your choice at any time under "Cookie preferences" at the foot of the page.',
        privacy: 'Privacy Policy',
        reject: 'Decline',
        accept: 'Accept',
    },
    de: {
        message: 'Mit Ihrer Einwilligung verwenden wir Google-Analytics-Cookies, um Besuche zu messen. Ihre Wahl können Sie jederzeit unter „Cookie-Einstellungen" am Seitenende ändern.',
        privacy: 'Datenschutzerklärung',
        reject: 'Ablehnen',
        accept: 'Akzeptieren',
    },
}

/**
 * Reads the stored consent choice.
 * Storage can be unavailable (privacy modes, blocked site data); that is treated
 * as "no choice yet" so the banner is shown rather than the page crashing.
 *
 * @returns {string | null}
 */
function readStoredChoice() {
    try {
        return window.localStorage.getItem(CONSENT_STORAGE_KEY)
    } catch {
        return null
    }
}

/**
 * Persists the choice. When storage is unavailable the choice still applies for
 * this page view (banner hidden, consent updated); it just cannot be remembered.
 *
 * @param {string} choice
 */
function storeChoice(choice) {
    try {
        window.localStorage.setItem(CONSENT_STORAGE_KEY, choice)
    } catch {
        // Nothing to persist into; the in-memory state below still applies the choice.
    }
}

/**
 * Cookie consent banner shared by the TR / EN / DE apps.
 *
 * Rendered in the first frame: no entrance animation, so the prerendered markup
 * paints immediately. Visitors who already chose are kept from seeing the
 * prerendered copy by the `html[data-cookie-consent]` guard in index.html + CSS.
 *
 * @param {{ lang?: 'tr' | 'en' | 'de', onOpenPrivacy: () => void }} props
 */
export default function CookieBanner({ lang = 'tr', onOpenPrivacy }) {
    const t = translations[lang] || translations.tr
    const [hasChoice, setHasChoice] = useState(() => Boolean(readStoredChoice()))

    // The footer's "cookie preferences" key reopens the banner (a choice can be
    // withdrawn as easily as it was given).
    useEffect(() => {
        const reopen = () => {
            document.documentElement.removeAttribute('data-cookie-consent')
            setHasChoice(false)
        }
        window.addEventListener(CONSENT_REOPEN_EVENT, reopen)
        return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen)
    }, [])

    if (hasChoice) return null

    const choose = (choice) => {
        storeChoice(choice)
        // Same marker index.html sets for returning visitors; other chrome
        // (the phone RFQ bar) waits for it so it never stacks on the banner.
        document.documentElement.setAttribute('data-cookie-consent', '')
        setHasChoice(true)
        // Accepting starts measurement here (gtag.js, this page's view);
        // declining stops it and deletes the analytics cookies.
        applyConsent(choice)
    }

    return (
        <div className="cookie-banner">
            <div className="cookie-content">
                <p>
                    {t.message}
                    <button type="button" className="cookie-link" onClick={onOpenPrivacy}>
                        {t.privacy}
                    </button>
                </p>
                <div className="cookie-buttons">
                    <button type="button" className="cookie-reject" onClick={() => choose(CONSENT_REJECTED)}>
                        {t.reject}
                    </button>
                    <button type="button" className="cookie-accept" onClick={() => choose(CONSENT_ACCEPTED)}>
                        {t.accept}
                    </button>
                </div>
            </div>
        </div>
    )
}
